import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { usePortalTarget } from "@/hooks/usePortalTarget";
import { useTrackContext } from "@/state/TrackContext";
import { useUrlParams } from "@/hooks/useUrlParams";
import { ICartItemHeadless, TBottomSummaryNav, TMultiTrackEntry, TSelectedVariants, TTrackTypes } from "@/types";
import { useRegisterBottomSummary } from "@/hooks/useRegisterBottomSummary";
import { bottomSummaryOnBeforeNavigateRef } from "@/shared/bottomSummaryCallbacks";
import mainTrackBanner from "@/assets/images/main_track_.jpg";
import miniTrackBanner from "@/assets/images/miniTrack_.jpeg";
import { handleAddToCartClick } from "@/shared/utils/cartOperations";
import { appendPersistedQueryParams } from "@/shared/utils/campaignParams";
import { useCartData } from "./hooks/useCartData";
import { useSelectedLineItems } from "./hooks/useSelectedLineItems";
import { useSummaryHeight } from "./hooks/useSummaryHeight";
import { useEditMode } from "./hooks/useEditMode";
import { useSessionManagement } from "./hooks/useSessionManagement";
import { useTrackSessions } from "./hooks/useTrackSessions";
import { ROUTES } from "@/consts";
import { NavigationButtons } from "./components/NavigationButtons";
import { TrackCard } from "./components/TrackCard";
import { RemoveConfirmationModal } from "./components/RemoveConfirmationModal";
import { TrackSelector } from "./components/TrackSelector";
import { SummaryHeader } from "./components/SummaryHeader";
import { getAdminIdFromStoreFrontId } from "@/shared/utils/shopify";
import { query } from "@/lib";
import { ADD_TO_CART_LINES } from "@/lib/shopify/queries";
import { parseDateFromTitle } from "@/shared/utils/productOperations";
import { getEffectivePostcodeDiscount } from "@/shared/utils/postcodeDiscount";
import { getSessionUnitPrice, isLargeGroupBooking } from "@/shared/utils/sessionPrice";
import { TRACK_KEYS } from "./utils/trackHelpers";
import { getMembershipDisplayPriceAdjustment } from "./utils/cartHelpers";
import { BookingAnalytics, StickySummaryInteraction, StickySummaryToggleTrigger } from "@/shared/utils/bookingAnalytics";
import { MdKeyboardArrowDown } from "react-icons/md";
import { usePathwayStyling } from "@/hooks/usePathwayStyling";
import { getRouteSlug, ROUTE_SLUGS, SCREEN_SLUGS } from "@/shared/utils/bookingUrl";
import { publishStickyFooterHeight } from "./stickyFooterHeightStore";
import { shouldSkipExperienceSelector } from "@/consts/featureFlags";

interface IBottomSummaryProps {
  previousInfo: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
    component?: React.ReactElement;
  };
  /** Omit on screens that commit on a single tap (the experience selector and the
      age-group step): they navigate straight away, so a Next button is unreachable. */
  nextInfo?: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
    component?: React.ReactElement;
    onBeforeNavigate?: () => void;
  };
  /** Optional override for experience-style mobile nav (otherwise derived from route). */
  mobileNavVariant?: "default" | "experience";
  /** Tile container whose tiles the Next/Checkout pill lines its edges up with on phones. */
  actionAlignRef?: React.RefObject<HTMLElement>;
}

const BottomSummaryView = ({ nav }: { nav: TBottomSummaryNav }) => {
  const previousInfo = nav.previousInfo;
  const nextInfo = nav.nextInfo;
  const mobileNavVariant: "default" | "experience" = nav.mobileNavVariant ?? "default";
  const {
    trackSelectionOption,
    mainTrackSelection,
    battleKartSelection,
    miniTrackSection,
    productRenderedLocation,
    juniorTrackSelection,
    intermediateTrackSelection,
    arcadeSelection,
    headlessCartTotalAmount,
    shopifyCartVariants,
    customerInfo: { isCustomerLogged, isCustomerActiveSubscriber },
    headlessCart,
    headlessCartInfo,
    membershipInfo,
    isAnyItemsAvailableInCart,
    showArcadeBanner,
    appHostedAt,
    visitorLocation,
    hasSummaryBeenAutoShown,
    isSummaryExpanded,
    multiTrackQueue,
    metaObjectMainTrackSectionConfig,
    metaObjectMiniTrackSectionConfig,
    metaObjectJuniorTrackSectionConfig,
    metaObjectIntermediateTrackSectionConfig,
    metaObjectBattleKartTrackSectionConfig,
    bottomSummaryConfig,
    dispatch,
  } = useTrackContext();

  const location = useLocation();
  const { navigateWithParams, getUrlParams } = useUrlParams();
  const { pathwayStyle, isBattleKart: isPathwayBattleKart } = usePathwayStyling();

  /** True on /. Derived from path so it flips synchronously on navigation —
      do NOT key off the registered nav config, which lags one frame and
      caused the mobile Checkout pill to flicker between default/experience
      styling when returning to home from another screen. */
  const isOnExperienceSelectionPage = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
    return normalizedPath === "/";
  }, [location.pathname]);

  // Clamp on /: the expanded cart popup must stay collapsed regardless of
  // whatever the previous screen set (e.g. date-time auto-expands on mobile),
  // so root only shows the floating Checkout/Next pill, not the cart panel.
  const isTotalVisible = isSummaryExpanded && !isOnExperienceSelectionPage;
  const setIsTotalVisible = useCallback(
    (value: boolean) => {
      dispatch({
        type: "SET_IS_SUMMARY_EXPANDED",
        payload: { isSummaryExpanded: value },
      });
    },
    [dispatch],
  );
  const resetSelectedTrack = useCallback(() => {
    dispatch({ type: "RESET_SELECTED_TRACK" });
  }, [dispatch]);

  const getTrackBanner = (entry: TMultiTrackEntry): string => {
    switch (entry.trackType) {
      case "MINI_TRACK":
        return miniTrackBanner;
      default:
        return mainTrackBanner;
    }
  };

  const navigateToNextQueuedTrack = useCallback(() => {
    const { entries, currentIndex } = multiTrackQueue;
    const nextIndex = currentIndex + 1;

    if (nextIndex >= entries.length) {
      dispatch({ type: "CLEAR_MULTI_TRACK_QUEUE" });
      resetSelectedTrack();
      navigateWithParams(ROUTES.INDEX, undefined, undefined, { directionOverride: 1 });
      return;
    }

    const nextEntry = entries[nextIndex];
    dispatch({ type: "ADVANCE_MULTI_TRACK" });

    dispatch({
      type: "SET_AGE_GROUP",
      payload: {
        selectedTrack: nextEntry.trackType,
        ageGroup: nextEntry.ageGroup,
      },
    });

    dispatch({
      type: "SET_SELECTED_TRACK",
      payload: {
        selectedTrackTitle: nextEntry.trackType,
        trackBanner: getTrackBanner(nextEntry),
      },
    });

    const navParams = nextEntry.key === "15+" ? { ageGroup: nextEntry.ageGroup || undefined } : undefined;
    navigateWithParams(nextEntry.route, navParams, undefined, { directionOverride: 1 });
  }, [multiTrackQueue, dispatch, resetSelectedTrack, navigateWithParams]);

  const hasMoreTracksInQueue = useMemo(() => {
    const { entries, currentIndex } = multiTrackQueue;
    return entries.length > 0 && currentIndex + 1 < entries.length;
  }, [multiTrackQueue]);

  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);

  const urlParams = getUrlParams();
  const cartData = useCartData();
  const { summaryHeight, isMobile, summaryRef } = useSummaryHeight();
  const stickyRootRef = useRef<HTMLDivElement>(null);

  // The mobile footer (SummaryHeader action rows) is shown for everything below the `desktop`
  // Tailwind breakpoint (1380px) — the desktop NavigationButtons only appear at `desktop:` and
  // up. `isMobile` from useSummaryHeight is ≤1024, so iPad landscape (1025–1379px) would other-
  // wise fall back to the compact "default" pill. Track "below desktop" so the experience/mobile
  // footer treatment (styled pill, transparent bar) applies across all iPad sizes too.
  const [isBelowDesktop, setIsBelowDesktop] = useState<boolean>(() => (typeof window !== "undefined" ? window.innerWidth < 1380 : true));
  useEffect(() => {
    const onResize = () => setIsBelowDesktop(window.innerWidth < 1380);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isRacingGamesTrackAgeStep = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
    return (
      (normalizedPath === `/${ROUTES.RACING_GAMES_TRACK.INDEX}` || urlParams.selectedRoute === ROUTES.RACING_GAMES_TRACK.INDEX) &&
      !urlParams.selectedScreen
    );
  }, [location.pathname, urlParams.selectedRoute, urlParams.selectedScreen]);

  const isSeatsRacesStep = urlParams.selectedScreen === SCREEN_SLUGS.SEATS;

  /** BK players step — same mobile footer structure as seats on other tracks. */
  const isBattleKartPlayersSessionsStep = urlParams.selectedScreen === SCREEN_SLUGS.PLAYERS;

  /** Any track date step (main, mini, junior, battle kart, intermediate, racing games). */
  const isDateTimeStep = urlParams.selectedScreen === SCREEN_SLUGS.DATE;

  const isPostCodeStep = urlParams.selectedScreen === SCREEN_SLUGS.LOCATION;

  const isArcadeStep = urlParams.selectedRoute === ROUTE_SLUGS.ARCADE;

  const effectiveMobileNavVariant: "default" | "experience" = useMemo(() => {
    // Apply the mobile footer treatment across the whole range where the mobile footer renders
    // (below the desktop breakpoint), so iPad landscape matches phones/tablet-portrait.
    if (!isBelowDesktop) {
      return "default";
    }
    if (mobileNavVariant === "experience") {
      return "experience";
    }
    return isRacingGamesTrackAgeStep ||
      isSeatsRacesStep ||
      isBattleKartPlayersSessionsStep ||
      isDateTimeStep ||
      isPostCodeStep ||
      isArcadeStep ||
      isOnExperienceSelectionPage
      ? "experience"
      : "default";
  }, [
    isBelowDesktop,
    mobileNavVariant,
    isRacingGamesTrackAgeStep,
    isSeatsRacesStep,
    isBattleKartPlayersSessionsStep,
    isDateTimeStep,
    isPostCodeStep,
    isArcadeStep,
    isOnExperienceSelectionPage,
  ]);

  // Collapse expanded cart before paint when landing on / so the panel does not flash over the experience grid.
  useLayoutEffect(() => {
    if (isOnExperienceSelectionPage && isSummaryExpanded) {
      dispatch({
        type: "SET_IS_SUMMARY_EXPANDED",
        payload: { isSummaryExpanded: false },
      });
    }
  }, [isOnExperienceSelectionPage, isSummaryExpanded, dispatch]);

  const selectedTrack: TTrackTypes = useMemo(() => {
    const { title } = trackSelectionOption;
    return title as TTrackTypes;
  }, [trackSelectionOption]);

  const selectedLineItems = useSelectedLineItems(cartData);

  // Track selections for hooks
  const trackSelections = {
    mainTrackSelection,
    battleKartSelection,
    miniTrackSection,
    juniorTrackSelection,
    intermediateTrackSelection,
  };

  const seatsTitleByTrack: Record<string, string> = useMemo(
    () => ({
      [TRACK_KEYS.MAIN]: metaObjectMainTrackSectionConfig?.seatsAndRacesSection?.seatsTitle || "Drivers",
      [TRACK_KEYS.BK]: metaObjectBattleKartTrackSectionConfig?.seatsAndRacesSection?.seatsTitle || "Players",
      [TRACK_KEYS.MINI]: metaObjectMiniTrackSectionConfig?.seatsAndRacesSection?.seatsTitle || "Drivers",
      [TRACK_KEYS.JUNIOR]: metaObjectJuniorTrackSectionConfig?.seatsAndRacesSection?.seatsTitle || "Drivers",
      [TRACK_KEYS.INTERMEDIATE]: metaObjectIntermediateTrackSectionConfig?.seatsAndRacesSection?.seatsTitle || "Drivers",
      [TRACK_KEYS.MEMBERSHIP]: "Quantity",
      [TRACK_KEYS.ARCADE]: "Quantity",
    }),
    [
      metaObjectMainTrackSectionConfig,
      metaObjectBattleKartTrackSectionConfig,
      metaObjectMiniTrackSectionConfig,
      metaObjectJuniorTrackSectionConfig,
      metaObjectIntermediateTrackSectionConfig,
    ],
  );

  // Session management hook
  const { selectedSessionId, setSelectedSessionId, showRemoveConfirmation, sessionToRemove, isRemoving, handleRemoveSessionClick, handleConfirmRemove, handleCancelRemove } =
    useSessionManagement({
      cartData,
      productRenderedLocation,
      headlessCartInfo,
      membershipInfo,
      isCustomerLogged,
      isCustomerActiveSubscriber,
      dispatch,
      trackSelections,
    });

  // Edit mode hook
  const {
    isEditMode,
    editingTrackKey,
    editSessionTime,
    editPlayerCount,
    isSaving,
    editedQuantities,
    setEditSessionTime,
    setEditPlayerCount,
    handleSaveChanges,
    handleCancelEdit,
    incrementPlayers,
    decrementPlayers,
    getSessionQuantity,
    clearEditedQuantities,
  } = useEditMode({
    cartData,
    productRenderedLocation,
    headlessCartInfo,
    membershipInfo,
    isCustomerLogged,
    isCustomerActiveSubscriber,
    dispatch,
    trackSelections,
  });

  const cartItemCount = useMemo(() => {
    const sumItemQuantities = (items: any[]) => items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    const fromCart = Math.max(sumItemQuantities(headlessCart), sumItemQuantities(cartData));
    if (fromCart > 0) {
      return fromCart;
    }

    if (selectedLineItems.length === 0) {
      return 0;
    }

    return selectedLineItems.reduce((sum, item) => {
      const variantId = item.variantsSelectedObject?.id;
      if (!variantId) {
        return sum + 1;
      }
      return sum + getSessionQuantity(variantId, 1);
    }, 0);
  }, [headlessCart, cartData, selectedLineItems, getSessionQuantity]);

  // Track sessions hook
  const { trackEntries, currentPage, setCurrentPage } = useTrackSessions({
    selectedLineItems,
    trackSelections,
    selectedSessionId,
  });

  // Navigation back to selection page based on track type
  const navigateToTrackSelection = useCallback(
    (trackKey: string, selectedDateParam?: string) => {
      const routeMap: Record<string, string> = {
        MAIN: ROUTES.MAIN_TRACK.DATE_TIME,
        BK: ROUTES.BATTLE_KART_TRACK.DATE_TIME,
        MINI: ROUTES.MINI_TRACK.DATE_TIME,
        JUNIOR: ROUTES.JUNIOR_TRACK.DATE_TIME,
        INTERMEDIATE: ROUTES.INTERMEDIATE_TRACK.DATE_TIME,
        ARCADE: ROUTES.ARCADE.ARCADE_CARD,
      };

      const route = routeMap[trackKey];
      if (route) {
        // Include selectedDate in URL params if provided
        if (selectedDateParam) {
          navigateWithParams("/" + route, { selectedDate: selectedDateParam });
        } else {
          navigateWithParams("/" + route);
        }
      }
    },
    [navigateWithParams],
  );

  // Sync previousInfo into context so ProgressiveBar can use it for back navigation.
  // No cleanup needed: ProgressiveBar hides the back button on home (no selectedRoute),
  // and every non-home page mounts its own BottomSummary that overwrites this value.
  useEffect(() => {
    dispatch({
      type: "SET_NAVIGATION_PREVIOUS_INFO",
      payload: {
        navigationPreviousInfo: {
          pageName: previousInfo.pageName,
          pagePath: previousInfo.pagePath,
        },
      },
    });
  }, [previousInfo.pageName, previousInfo.pagePath, dispatch]);

  // Reset auto-shown flag and clear track order when cart becomes empty
  useEffect(() => {
    if (!isAnyItemsAvailableInCart && hasSummaryBeenAutoShown) {
      dispatch({
        type: "SET_HAS_SUMMARY_BEEN_AUTO_SHOWN",
        payload: { hasSummaryBeenAutoShown: false },
      });
      // Clear track order from localStorage when cart is emptied
      import("./utils/trackOrderStorage").then(({ clearTrackOrder }) => {
        clearTrackOrder();
      });
    }
  }, [isAnyItemsAvailableInCart, hasSummaryBeenAutoShown, dispatch]);

  // Fetch and update cart total amount on mount if cart has items
  useEffect(() => {
    const updateCartTotal = async () => {
      if (isAnyItemsAvailableInCart && (!headlessCartTotalAmount || headlessCartTotalAmount === "" || headlessCartTotalAmount === "0.00")) {
        try {
          if (productRenderedLocation !== "external") {
            const response = await fetch("/cart.json");
            if (response.ok) {
              const cartInfo = await response.json();
              const { total_price, items_subtotal_price } = cartInfo;
              if (total_price || items_subtotal_price) {
                dispatch({
                  type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                  payload: { headlessCartTotalAmount: total_price || items_subtotal_price },
                });
              }
            }
          }
        } catch (error) {
          console.error("Error fetching cart total:", error);
        }
      }
    };

    updateCartTotal();
  }, [isAnyItemsAvailableInCart, headlessCartTotalAmount, productRenderedLocation, dispatch]);

  // Fix cart $0 bug - Refresh cart data when component mounts or location changes
  useEffect(() => {
    const refreshCartData = async () => {
      // Only refresh if we have items in cart but the total is 0
      if (isAnyItemsAvailableInCart && headlessCartTotalAmount === "0.00") {
        try {
          if (productRenderedLocation === "external") {
            // For headless cart, the data should already be in context
            // If it's showing 0, we might need to recalculate
            if (headlessCart && headlessCart.length > 0) {
              const total = headlessCart.reduce((sum: number, item: any) => {
                const price = Number(item.merchandise?.priceV2?.amount || 0);
                const quantity = item.quantity || 1;
                return sum + price * quantity;
              }, 0);

              if (total > 0) {
                dispatch({
                  type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                  payload: { headlessCartTotalAmount: total.toString() },
                });
              }
            }
          } else {
            // For Shopify cart, fetch cart.json
            const cartDataResponse = await fetch("/cart.json");
            const cartInfo = await cartDataResponse.json();

            if (cartInfo) {
              const { total_price, items_subtotal_price } = cartInfo;
              if (total_price || items_subtotal_price) {
                dispatch({
                  type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                  payload: { headlessCartTotalAmount: total_price || items_subtotal_price },
                });
              }
            }
          }
        } catch (error) {
          console.error("Error refreshing cart data:", error);
        }
      }
    };

    refreshCartData();
  }, [location.pathname, isAnyItemsAvailableInCart, headlessCartTotalAmount, productRenderedLocation, headlessCart, dispatch]);

  const totalAmount: number = useMemo(() => {
    const baseTotal = productRenderedLocation === "external" ? Number(headlessCartTotalAmount) : Number(Number(headlessCartTotalAmount) / 100);

    // SessionPills prices cart-backed lines straight from the cart (real line subtotals,
    // including any discount Shopify already applied — e.g. the visitor/postcode discount,
    // which Shopify clamps to each track's minimum price). Membership pricing is the only
    // display-only discount not reflected in the cart subtotal, so it is the only per-unit
    // amount bridged here (its display price folds the visitor discount in, hence the sum).
    // Deducting the visitor discount on its own pushed the Total below the pills' Sub Totals
    // whenever Shopify didn't discount a line (e.g. floor-priced $19 main-track sessions).
    const membershipDisplayDiscountPerUnit = membershipInfo.isMembershipPriceEnabled === true ? 19 + (visitorLocation.discountAmount || 0) : 0;
    const membershipTrackAdjustment = getMembershipDisplayPriceAdjustment(cartData, productRenderedLocation, membershipDisplayDiscountPerUnit);

    const adjustedBaseTotal = Math.max(0, baseTotal - membershipTrackAdjustment);

    const allSelectedVariants = [
      ...mainTrackSelection.variantsAndDate.selectedVariants,
      ...battleKartSelection.variantsAndDate.selectedVariants,
      ...miniTrackSection.variantsAndDate.selectedVariants,
      ...juniorTrackSelection.variantsAndDate.selectedVariants,
      ...intermediateTrackSelection.variantsAndDate.selectedVariants,
    ];

    let selectedVariantsTotal = 0;
    const pendingMainTrackLineTotals: number[] = [];
    const pendingBattleKartLineTotals: number[] = [];
    const cartVariantIds = new Set<string>();

    if (productRenderedLocation === "external") {
      headlessCart.forEach((item: any) => {
        cartVariantIds.add(item.merchandise?.id);
      });
    } else {
      // Use actual cart data instead of shopifyCartVariants to ensure we're checking against real cart items
      cartData.forEach((item: any) => {
        // For Shopify cart items, construct the variant ID in the same format as selectedVariants
        const variantId = `gid://shopify/ProductVariant/${item.variant_id}`;
        cartVariantIds.add(variantId);
      });
    }

    const shouldApplyMembershipPrice = (variant: TSelectedVariants): boolean => {
      const productType = (variant.variantsSelectedObject?.product?.productType || "").toLowerCase();
      const isMembershipEligibleTrack =
        productType.includes("main") ||
        productType.includes("mini") ||
        productType.includes("junior") ||
        productType.includes("intermediate") ||
        productType.includes("battlekart") ||
        productType.includes("battle kart");
      return isMembershipEligibleTrack && membershipInfo.isMembershipPriceEnabled === true;
    };

    const getTrackTypeForVariant = (variant: TSelectedVariants) => {
      const productType = (variant.variantsSelectedObject?.product?.productType || "").toLowerCase();

      if (productType.includes("battlekart") || productType.includes("battle kart")) return "BATTLE_KART_TRACK" as const;
      if (productType.includes("mini")) return "MINI_TRACK" as const;
      if (productType.includes("junior")) return "JUNIOR_TRACK" as const;
      if (productType.includes("intermediate")) return "INTERMEDIATE_TRACK" as const;
      if (productType.includes("main")) return "MAIN_TRACK" as const;

      return null;
    };

    // `seats` is the track's seats-screen count, which decides large-group pricing.
    const getDiscountedDisplayPrice = (variant: TSelectedVariants, seats: number): number => {
      const basePrice = Number(variant.variantsSelectedObject.priceV2?.amount || variant.variantsSelectedObject.price || variant.variantsSelectedObject.unitPrice?.amount || 0);
      const sessionPrice = getSessionUnitPrice({
        basePrice,
        postcodeDiscount: getEffectivePostcodeDiscount(visitorLocation.discountAmount || 0, variant.variantsSelectedObject.postcodeDiscountAdjustment),
        isLargeGroup: isLargeGroupBooking(seats),
        track: getTrackTypeForVariant(variant),
      });

      return shouldApplyMembershipPrice(variant) ? Math.max(0, sessionPrice - 19) : sessionPrice;
    };

    const shouldDisableRaceDiscounts = (isCustomerLogged && isCustomerActiveSubscriber) || membershipInfo.isMembershipItemAddedToCart;

    const applyPreviewRaceDiscounts = (lineTotals: number[], isMainTrackDiscount: boolean): number => {
      if (lineTotals.length === 0) {
        return 0;
      }

      if (shouldDisableRaceDiscounts) {
        return lineTotals.reduce((sum, value) => sum + value, 0);
      }

      const hasFullQualification = isMainTrackDiscount
        ? urlParams.qualifiedFor20 === "true" && urlParams.qualifiedFor25 === "true"
        : urlParams.bkQualifiedFor20 === "true" && urlParams.bkQualifiedFor25 === "true";

      const is20OnThirdPath = isMainTrackDiscount
        ? !hasFullQualification && urlParams.qualifiedFor20OnThird === "true" && urlParams.discount20Applied === "true" && urlParams.discount25Applied !== "true"
        : !hasFullQualification && urlParams.bkQualifiedFor20OnThird === "true" && urlParams.bkDiscount20Applied === "true" && urlParams.bkDiscount25Applied !== "true";

      const has25Discount = isMainTrackDiscount ? hasFullQualification || urlParams.discount25Applied === "true" : hasFullQualification || urlParams.bkDiscount25Applied === "true";

      const has20Discount = isMainTrackDiscount ? hasFullQualification || urlParams.discount20Applied === "true" : hasFullQualification || urlParams.bkDiscount20Applied === "true";

      // 20% = 2nd race (needs >= 2 races), 25% = 3rd race (needs >= 3 races). Gate on the
      // actual item count so a stale discount25Applied flag doesn't apply the 3rd-race
      // discount when only 2 items are selected.
      const canApply20 = has20Discount && lineTotals.length >= 2;
      const canApply25 = has25Discount && lineTotals.length >= 3;

      if (!canApply20 && !canApply25) {
        return lineTotals.reduce((sum, value) => sum + value, 0);
      }

      const sortedIndexes = lineTotals
        .map((lineTotal, index) => ({ index, lineTotal }))
        .sort((a, b) => a.lineTotal - b.lineTotal)
        .map((item) => item.index);

      const discountedLineTotals = [...lineTotals];
      const cheapestIndex = sortedIndexes[0];
      const secondCheapestIndex = sortedIndexes[1];

      if (is20OnThirdPath && canApply20) {
        if (cheapestIndex !== undefined) {
          discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.8;
        }
      } else if (canApply25 && canApply20) {
        if (cheapestIndex !== undefined) {
          discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.75;
        }
        if (secondCheapestIndex !== undefined) {
          discountedLineTotals[secondCheapestIndex] = discountedLineTotals[secondCheapestIndex] * 0.8;
        }
      } else if (canApply20) {
        if (cheapestIndex !== undefined) {
          discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.8;
        }
      } else if (canApply25) {
        if (cheapestIndex !== undefined) {
          discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.75;
        }
      }

      return discountedLineTotals.reduce((sum, value) => sum + value, 0);
    };

    allSelectedVariants.forEach((variant) => {
      const variantId = variant.variantsSelectedObject.id;
      const isInCart = cartVariantIds.has(variantId);

      if (!isInCart) {
        // Keep pending-variant quantity in sync with session pill subtotal quantities
        const productType = (variant.variantsSelectedObject?.product?.productType || "").toLowerCase();
        const defaultPendingQuantity = productType.includes("battlekart")
          ? battleKartSelection.racesSeats.numberOfSeats
          : productType.includes("mini")
            ? miniTrackSection.racesSeats.numberOfSeats
            : productType.includes("junior")
              ? juniorTrackSelection.racesSeats.numberOfSeats
              : productType.includes("intermediate")
                ? intermediateTrackSelection.racesSeats.numberOfSeats
                : mainTrackSelection.racesSeats.numberOfSeats;
        const quantity = editedQuantities[variantId] || defaultPendingQuantity || 1;
        const lineTotal = getDiscountedDisplayPrice(variant, defaultPendingQuantity) * quantity;
        selectedVariantsTotal += lineTotal;

        if (productType.includes("main")) {
          pendingMainTrackLineTotals.push(lineTotal);
        } else if (productType.includes("battlekart") || productType.includes("battle kart")) {
          pendingBattleKartLineTotals.push(lineTotal);
        }
      }
    });

    if (pendingMainTrackLineTotals.length > 0) {
      const nonDiscountedMainTotal = pendingMainTrackLineTotals.reduce((sum, value) => sum + value, 0);
      const discountedMainTotal = applyPreviewRaceDiscounts(pendingMainTrackLineTotals, true);
      selectedVariantsTotal += discountedMainTotal - nonDiscountedMainTotal;
    }

    if (pendingBattleKartLineTotals.length > 0) {
      const nonDiscountedBKTotal = pendingBattleKartLineTotals.reduce((sum, value) => sum + value, 0);
      const discountedBKTotal = applyPreviewRaceDiscounts(pendingBattleKartLineTotals, false);
      selectedVariantsTotal += discountedBKTotal - nonDiscountedBKTotal;
    }

    return adjustedBaseTotal + selectedVariantsTotal;
  }, [
    productRenderedLocation,
    headlessCartTotalAmount,
    isCustomerLogged,
    isCustomerActiveSubscriber,
    membershipInfo.isMembershipPriceEnabled,
    membershipInfo.isMembershipItemAddedToCart,
    headlessCart,
    cartData,
    mainTrackSelection.racesSeats.numberOfSeats,
    battleKartSelection.racesSeats.numberOfSeats,
    miniTrackSection.racesSeats.numberOfSeats,
    juniorTrackSelection.racesSeats.numberOfSeats,
    intermediateTrackSelection.racesSeats.numberOfSeats,
    mainTrackSelection.variantsAndDate.selectedVariants,
    miniTrackSection.variantsAndDate.selectedVariants,
    juniorTrackSelection.variantsAndDate.selectedVariants,
    intermediateTrackSelection.variantsAndDate.selectedVariants,
    battleKartSelection.variantsAndDate.selectedVariants,
    editedQuantities,
    visitorLocation.discountAmount,
    urlParams.discount20Applied,
    urlParams.discount25Applied,
    urlParams.qualifiedFor20,
    urlParams.qualifiedFor25,
    urlParams.qualifiedFor20OnThird,
    urlParams.bkDiscount20Applied,
    urlParams.bkDiscount25Applied,
    urlParams.bkQualifiedFor20,
    urlParams.bkQualifiedFor25,
    urlParams.bkQualifiedFor20OnThird,
  ]);

  const shouldDisableRaceDiscounts = (isCustomerLogged && isCustomerActiveSubscriber) || membershipInfo.isMembershipItemAddedToCart;

  const hasAnyRaceDiscounts = useMemo(() => {
    if (shouldDisableRaceDiscounts) return false;

    const hasMainFullQualification = urlParams.qualifiedFor20 === "true" && urlParams.qualifiedFor25 === "true";
    const hasMain20Discount = hasMainFullQualification || urlParams.discount20Applied === "true";
    const hasMain25Discount = hasMainFullQualification || urlParams.discount25Applied === "true";

    const hasBkFullQualification = urlParams.bkQualifiedFor20 === "true" && urlParams.bkQualifiedFor25 === "true";
    const hasBk20Discount = hasBkFullQualification || urlParams.bkDiscount20Applied === "true";
    const hasBk25Discount = hasBkFullQualification || urlParams.bkDiscount25Applied === "true";

    return hasMain20Discount || hasMain25Discount || hasBk20Discount || hasBk25Discount;
  }, [
    shouldDisableRaceDiscounts,
    urlParams.qualifiedFor20,
    urlParams.qualifiedFor25,
    urlParams.discount20Applied,
    urlParams.discount25Applied,
    urlParams.bkQualifiedFor20,
    urlParams.bkQualifiedFor25,
    urlParams.bkDiscount20Applied,
    urlParams.bkDiscount25Applied,
  ]);

  const totalAmountDisplay = useMemo(() => {
    if (hasAnyRaceDiscounts) {
      return totalAmount.toFixed(2);
    }
    const rounded = Math.round(totalAmount * 100) / 100;
    return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2);
  }, [hasAnyRaceDiscounts, totalAmount]);

  const shouldShowSummaryBox: boolean = useMemo(() => {
    return isDateTimeStep || totalAmount > 0 || selectedLineItems.length > 0 || shopifyCartVariants.length > 0 || isAnyItemsAvailableInCart;
  }, [isDateTimeStep, totalAmount, selectedLineItems.length, shopifyCartVariants.length, isAnyItemsAvailableInCart]);

  const isSummaryEmpty: boolean = useMemo(() => {
    return totalAmount === 0 && selectedLineItems.length === 0 && shopifyCartVariants.length === 0 && !isAnyItemsAvailableInCart;
  }, [totalAmount, selectedLineItems.length, shopifyCartVariants.length, isAnyItemsAvailableInCart]);

  // Expand sticky summary on time selection page on mobile once sessions are selected
  useEffect(() => {
    if (isMobile && isDateTimeStep && shouldShowSummaryBox && !isSummaryEmpty) {
      dispatch({
        type: "SET_IS_SUMMARY_EXPANDED",
        payload: { isSummaryExpanded: true },
      });
    }
  }, [isMobile, isDateTimeStep, shouldShowSummaryBox, isSummaryEmpty, dispatch]);

  // Publish measured sticky height (bar + expanded panel) so the date-time viewport
  // can reserve matching clearance. The panel is position:absolute, so root height alone is not enough.
  useEffect(() => {
    const root = stickyRootRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;

    const publish = () => {
      const rootRect = root.getBoundingClientRect();
      const panel = root.querySelector<HTMLElement>("[data-sticky-summary-panel]");
      if (panel && isTotalVisible) {
        const panelRect = panel.getBoundingClientRect();
        const top = Math.min(panelRect.top, rootRect.top);
        publishStickyFooterHeight(Math.max(0, rootRect.bottom - top));
        return;
      }
      publishStickyFooterHeight(rootRect.height);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(root);
    const panel = root.querySelector<HTMLElement>("[data-sticky-summary-panel]");
    if (panel) observer.observe(panel);
    window.addEventListener("resize", publish);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", publish);
    };
  }, [isTotalVisible, isSummaryEmpty, shouldShowSummaryBox, isMobile, isDateTimeStep]);

  const currentQuantity: number = useMemo(() => {
    if (trackSelectionOption.title === "MAIN_TRACK") {
      return mainTrackSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      return battleKartSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      return miniTrackSection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
      return juniorTrackSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "INTERMEDIATE_TRACK") {
      return intermediateTrackSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "ARCADE") {
      return arcadeSelection.arcadeGameChoose.selectedQuantity;
    }
    return 0;
  }, [
    trackSelectionOption.title,
    mainTrackSelection.racesSeats.numberOfSeats,
    battleKartSelection.racesSeats.numberOfSeats,
    miniTrackSection.racesSeats.numberOfSeats,
    juniorTrackSelection.racesSeats.numberOfSeats,
    intermediateTrackSelection.racesSeats.numberOfSeats,
    arcadeSelection.arcadeGameChoose.selectedQuantity,
  ]);

  const currentNumberOfRaces: number = useMemo(() => {
    if (trackSelectionOption.title === "MAIN_TRACK") {
      return mainTrackSelection.racesSeats.numberOfRaces;
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      return battleKartSelection.racesSeats.numberOfRaces;
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      return miniTrackSection.racesSeats.numberOfRaces;
    } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
      return juniorTrackSelection.racesSeats.numberOfRaces;
    } else if (trackSelectionOption.title === "INTERMEDIATE_TRACK") {
      return intermediateTrackSelection.racesSeats.numberOfRaces;
    }
    return 0;
  }, [
    trackSelectionOption.title,
    mainTrackSelection.racesSeats.numberOfRaces,
    battleKartSelection.racesSeats.numberOfRaces,
    miniTrackSection.racesSeats.numberOfRaces,
    juniorTrackSelection.racesSeats.numberOfRaces,
    intermediateTrackSelection.racesSeats.numberOfRaces,
  ]);

  const selectedVariants: TSelectedVariants[] = useMemo(() => {
    if (trackSelectionOption.title === "MAIN_TRACK") {
      return mainTrackSelection.variantsAndDate.selectedVariants;
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      return battleKartSelection.variantsAndDate.selectedVariants;
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      return miniTrackSection.variantsAndDate.selectedVariants;
    } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
      return juniorTrackSelection.variantsAndDate.selectedVariants;
    } else if (trackSelectionOption.title === "INTERMEDIATE_TRACK") {
      return intermediateTrackSelection.variantsAndDate.selectedVariants;
    }
    return [];
  }, [
    trackSelectionOption.title,
    mainTrackSelection.variantsAndDate.selectedVariants,
    battleKartSelection.variantsAndDate.selectedVariants,
    miniTrackSection.variantsAndDate.selectedVariants,
    juniorTrackSelection.variantsAndDate.selectedVariants,
    intermediateTrackSelection.variantsAndDate.selectedVariants,
  ]);

  const hasPendingSelections: boolean = useMemo(() => {
    if (selectedVariants.length === 0) return false;
    if (productRenderedLocation === "external") {
      const cartVariantIds = new Set(headlessCart.map((item: any) => item.merchandise?.id));
      return selectedVariants.some((variant) => !cartVariantIds.has(variant.variantsSelectedObject.id));
    }
    const cartVariantIds = new Set(cartData.map((item: any) => `gid://shopify/ProductVariant/${item.variant_id}`));
    return selectedVariants.some((variant) => !cartVariantIds.has(variant.variantsSelectedObject.id));
  }, [selectedVariants, productRenderedLocation, headlessCart, cartData]);

  /** Next is shown exactly when the screen that registered the footer offers one.
      Screens that commit on a single tap (the experience selector, the age-group
      step) omit nextInfo entirely. Keying off the registered config rather than the
      URL matters during a route transition: AnimatePresence mode="wait" keeps the
      outgoing screen's config mounted while location.pathname is already the new
      route, and reading the URL would pop a Next button onto a screen that has none. */
  const showNextButton = !!nextInfo;

  const showMobileCheckoutOnFooter = useMemo(
    () =>
      isDateTimeStep ||
      (isOnExperienceSelectionPage && isAnyItemsAvailableInCart && showArcadeBanner && !nextInfo?.isActive),
    [isDateTimeStep, isOnExperienceSelectionPage, isAnyItemsAvailableInCart, showArcadeBanner, nextInfo?.isActive],
  );

  // On / the only valid action is Checkout, and only once the cart has items. Back has
  // no destination there, so it is hidden — likewise on the racing age step while the
  // experience selector is skipped, since that makes it the first screen of the flow.
  // The nextInfo guards keep Checkout and a lingering Next from overlapping while the
  // outgoing screen's config is still registered during a route transition.
  const showDesktopBackButton =
    !isOnExperienceSelectionPage &&
    !(
      (isRacingGamesTrackAgeStep || previousInfo.pagePath === ROUTES.INDEX) &&
      shouldSkipExperienceSelector({ isAnyItemsAvailableInCart, showArcadeBanner, appHostedAt })
    );
  const showDesktopCheckoutButton =
    isOnExperienceSelectionPage && isAnyItemsAvailableInCart && showArcadeBanner && !nextInfo?.isActive;
  const showDesktopNavBar = showDesktopBackButton || showDesktopCheckoutButton || showNextButton;

  const isAllSessionsSelected: boolean = useMemo(() => {
    if (currentNumberOfRaces === 0 || selectedVariants.length === 0) return false;
    if (trackSelectionOption.title === "MAIN_TRACK") {
      if (membershipInfo && membershipInfo.eligibleVariants && membershipInfo.eligibleVariants.length > 0) {
        return selectedVariants.length + membershipInfo.eligibleVariants.length === currentNumberOfRaces;
      }
      return selectedVariants.length === currentNumberOfRaces;
    }
    return selectedVariants.length === currentNumberOfRaces;
  }, [trackSelectionOption.title, membershipInfo, selectedVariants.length, currentNumberOfRaces]);

  /** Sends a sticky summary interaction to GA with the current page/summary state attached. */
  const trackStickySummary = useCallback(
    (interaction: StickySummaryInteraction) => {
      BookingAnalytics.stickySummaryInteraction(interaction, {
        route: urlParams.selectedRoute || "home",
        screen: urlParams.selectedScreen || "index",
        track: selectedTrack || undefined,
        device: isMobile ? "mobile" : "desktop",
        isExpanded: isTotalVisible,
        cartItemCount,
      });
    },
    [urlParams.selectedRoute, urlParams.selectedScreen, selectedTrack, isMobile, isTotalVisible, cartItemCount],
  );

  const toggleTotal = (trigger: StickySummaryToggleTrigger) => {
    trackStickySummary({ action: isTotalVisible ? "summary_close" : "summary_open", trigger });
    setIsTotalVisible(!isTotalVisible);
  };

  const collapseSummary = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      trackStickySummary({ action: "summary_close", trigger: "minimise" });
      setIsTotalVisible(false);
    },
    [setIsTotalVisible, trackStickySummary],
  );

  /** Mobile expanded panel: chevron-only collapse control (Total lives on the cart icon). */
  const mobileExpandedMinimiseButton = (
    <button
      type="button"
      className="desktop:tw-hidden tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-text-white -tw-mr-[2px]"
      onClick={collapseSummary}
      aria-label="Hide cart summary"
    >
      <MdKeyboardArrowDown className="tw-shrink-0 tw-text-[22px] tw-leading-none" />
    </button>
  );

  const getTrackTypeFromPath = useCallback((): TTrackTypes | null => {
    const trackByRoute: Record<string, TTrackTypes> = {
      [ROUTE_SLUGS.MAIN]: "MAIN_TRACK",
      [ROUTE_SLUGS.MINI]: "MINI_TRACK",
      [ROUTE_SLUGS.JUNIOR]: "JUNIOR_TRACK",
      [ROUTE_SLUGS.INTERMEDIATE]: "INTERMEDIATE_TRACK",
      [ROUTE_SLUGS.BATTLE_KART]: "BATTLE_KART_TRACK",
    };
    return trackByRoute[getRouteSlug(location.pathname) ?? ""] ?? null;
  }, [location.pathname]);

  const clearSelectedVariantsForTrack = useCallback(() => {
    const trackTypeFromPath = getTrackTypeFromPath();
    const trackTypeToClear = trackTypeFromPath ?? trackSelectionOption.title;
    const resetVariantsAndDate = {
      selectedSessionTime: null,
      selectedDate: new Date(),
      selectedVariants: [],
      selectedVariantsForUpsell: [],
    };

    switch (trackTypeToClear) {
      case "MAIN_TRACK":
        dispatch({
          type: "SET_DATE_AND_VARIANT",
          payload: { variantsAndDate: resetVariantsAndDate },
        });
        break;
      case "MINI_TRACK":
        dispatch({
          type: "SET_DATE_AND_VARIANT_MINI_TRACK",
          payload: { variantsAndDate: resetVariantsAndDate },
        });
        break;
      case "JUNIOR_TRACK":
        dispatch({
          type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
          payload: { variantsAndDate: resetVariantsAndDate },
        });
        break;
      case "INTERMEDIATE_TRACK":
        dispatch({
          type: "SET_DATE_AND_VARIANT_INTERMEDIATE_TRACK",
          payload: { variantsAndDate: resetVariantsAndDate },
        });
        break;
      case "BATTLE_KART_TRACK":
        dispatch({
          type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
          payload: { variantsAndDate: resetVariantsAndDate },
        });
        break;
      default:
        break;
    }
  }, [dispatch, getTrackTypeFromPath, trackSelectionOption.title]);

  const checkoutRedirect = useCallback(() => {
    // Preserved landing-URL tracking/campaign params are merged into the
    // destination so Shopify Campaigns can attribute the sale.
    const baseUrl = productRenderedLocation === "external" && headlessCartInfo.checkoutUrl ? headlessCartInfo.checkoutUrl : "/checkout";
    window.location.href = appendPersistedQueryParams(baseUrl);
  }, [productRenderedLocation, headlessCartInfo.checkoutUrl]);

  // checkoutRedirect may leave the "Adding session..." state on while the browser
  // navigates away. If the user comes back via the back button, the page is restored
  // from the back/forward cache with that state frozen, so clear it on restore.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      dispatch({
        type: "SET_IS_PROCESSING_ADD_TO_CART",
        payload: { isProcessing: false, processingType: undefined },
      });
      setIsAddingToCart(false);
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [dispatch]);

  const handleNextButtonClick = useCallback(async () => {
    if (!nextInfo) return;

    // Check if we're on the Add Another Attraction page and need to add to cart
    const isOnAddAnotherAttractionPage = urlParams.selectedScreen === SCREEN_SLUGS.EXTRAS;
    const isArcadeTrack = selectedTrack === "ARCADE";

    if (isOnAddAnotherAttractionPage && nextInfo.pageName.toLowerCase().includes("add to cart")) {
      try {
        setIsAddingToCart(true);
        dispatch({
          type: "SET_IS_PROCESSING_ADD_TO_CART",
          payload: { isProcessing: true, processingType: "adding" },
        });

        // Handle arcade cards separately
        if (isArcadeTrack) {
          const { selectedVariantId, selectedQuantity } = arcadeSelection.arcadeGameChoose;
          let didAddToCart = false;

          if (productRenderedLocation === "external") {
            const lines = {
              items: [
                {
                  merchandiseId: selectedVariantId,
                  quantity: selectedQuantity,
                },
              ],
            };

            const addToCart = await query(ADD_TO_CART_LINES, {
              cartId: headlessCartInfo.id,
              lines: lines.items,
            });

            const isAddedToCart = addToCart?.data?.cartLinesAdd;
            if (isAddedToCart) {
              didAddToCart = true;
              const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
              dispatch({
                type: "SET_CARTITEMS_HEADLESS",
                payload: {
                  headlessCart: [...cartItems] as ICartItemHeadless[],
                },
              });

              const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
              dispatch({
                type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                payload: {
                  headlessCartTotalAmount: cartTotalAmount,
                },
              });
            }
          } else {
            let formData = {
              items: [
                {
                  id: getAdminIdFromStoreFrontId(selectedVariantId),
                  quantity: selectedQuantity,
                },
              ],
            };

            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(formData),
            });

            const cart = await response.json();
            if (cart) {
              didAddToCart = true;
              // Fetch updated cart data
              const cartResponse = await fetch("/cart.json");
              const cartData = await cartResponse.json();

              // Dispatch cart:updated event to notify all listeners
              const cartUpdatedEvent = new CustomEvent("cart:updated", {
                detail: { cart: cartData },
              });
              window.dispatchEvent(cartUpdatedEvent);
            }
          }

          if (!isMobile) {
            setIsTotalVisible(true);
          }
          dispatch({
            type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
            payload: { isAnyItemsAvailableInCart: true },
          });

          if (headlessCartTotalAmount !== "" && headlessCartTotalAmount !== "0.00") {
            dispatch({
              type: "SET_IS_IN_CART_MODE",
              payload: { isInCartMode: true },
            });
          }

          if (headlessCartTotalAmount === "" || (headlessCartTotalAmount === "0.00" && productRenderedLocation !== "external")) {
            const cartDataResponse = await fetch("/cart.json");
            const cartInfo = await cartDataResponse.json();

            if (cartInfo) {
              const { total_price, items_subtotal_price } = cartInfo;
              if (total_price || items_subtotal_price) {
                dispatch({
                  type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                  payload: { headlessCartTotalAmount: total_price || items_subtotal_price },
                });
                dispatch({
                  type: "SET_IS_IN_CART_MODE",
                  payload: { isInCartMode: true },
                });
              }
            }
          }

          if (didAddToCart) {
            BookingAnalytics.addToCartSuccess({
              track: "ARCADE",
              quantity: arcadeSelection.arcadeGameChoose.selectedQuantity,
              variantCount: 1,
              source: "bottom_summary",
            });
            dispatch({
              type: "SET_IS_SHOPIFY_CART_VARIANTS_LOADING",
              payload: { isShopifyCartVariantsLoaded: false },
            });
            resetSelectedTrack();
            navigateWithParams(ROUTES.INDEX, undefined, undefined, { directionOverride: 1 });
          } else {
            BookingAnalytics.addToCartFailure({
              track: "ARCADE",
              reason: "unknown",
              source: "bottom_summary",
            });
          }
        } else {
          // Handle other track types
          const result = await handleAddToCartClick(
            selectedVariants,
            currentQuantity,
            productRenderedLocation,
            headlessCart,
            headlessCartInfo,
            dispatch,
            false,
            visitorLocation,
            selectedTrack,
            {
              discountOffered: urlParams.discountOffered,
              discount20Applied: urlParams.discount20Applied,
              discount25Applied: urlParams.discount25Applied,
              qualifiedFor20: urlParams.qualifiedFor20,
              qualifiedFor25: urlParams.qualifiedFor25,
              qualifiedFor20OnThird: urlParams.qualifiedFor20OnThird,
              bkDiscountOffered: urlParams.bkDiscountOffered,
              bkDiscount20Applied: urlParams.bkDiscount20Applied,
              bkDiscount25Applied: urlParams.bkDiscount25Applied,
              bkQualifiedFor20: urlParams.bkQualifiedFor20,
              bkQualifiedFor25: urlParams.bkQualifiedFor25,
              bkQualifiedFor20OnThird: urlParams.bkQualifiedFor20OnThird,
            },
            editedQuantities, // Pass edited quantities for per-variant quantity overrides
          );

          if (result?.success) {
            BookingAnalytics.addToCartSuccess({
              track: selectedTrack,
              quantity: currentQuantity,
              variantCount: selectedVariants.length,
              source: "bottom_summary",
            });

            if (!isMobile) {
              setIsTotalVisible(true);
            }

            clearEditedQuantities();

            dispatch({
              type: "SET_IS_SHOPIFY_CART_VARIANTS_LOADING",
              payload: { isShopifyCartVariantsLoaded: false },
            });

            dispatch({
              type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
              payload: { isAnyItemsAvailableInCart: true },
            });

            if (headlessCartTotalAmount !== "" && headlessCartTotalAmount !== "0.00") {
              dispatch({
                type: "SET_IS_IN_CART_MODE",
                payload: { isInCartMode: true },
              });
            }

            if (headlessCartTotalAmount === "" || (headlessCartTotalAmount === "0.00" && productRenderedLocation !== "external")) {
              const cartDataResponse = await fetch("/cart.json");
              const cartInfo = await cartDataResponse.json();

              if (cartInfo) {
                const { total_price, items_subtotal_price } = cartInfo;
                if (total_price || items_subtotal_price) {
                  dispatch({
                    type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                    payload: { headlessCartTotalAmount: total_price || items_subtotal_price },
                  });
                  dispatch({
                    type: "SET_IS_IN_CART_MODE",
                    payload: { isInCartMode: true },
                  });
                }
              }
            }
          } else {
            BookingAnalytics.addToCartFailure({
              track: selectedTrack,
              reason: result?.error || "unknown",
              source: "bottom_summary",
            });
          }
        }

        dispatch({
          type: "SET_IS_PROCESSING_ADD_TO_CART",
          payload: { isProcessing: false, processingType: undefined },
        });
        setIsAddingToCart(false);
      } catch (error) {
        console.error(error);
        BookingAnalytics.addToCartFailure({
          track: selectedTrack,
          reason: error instanceof Error ? error.message : "exception",
          source: "bottom_summary",
        });
        dispatch({
          type: "SET_IS_PROCESSING_ADD_TO_CART",
          payload: { isProcessing: false, processingType: undefined },
        });
        setIsAddingToCart(false);
      }
    } else if (nextInfo.pageName.toLowerCase().includes("review") || nextInfo.pageName.toLowerCase().includes("next") || nextInfo.pageName.toLowerCase().includes("add to cart")) {
      try {
        // For arcade cards on selection page, add to cart, show mini cart, then return to selection
        if (isArcadeTrack && nextInfo.pageName.toLowerCase().includes("add to cart")) {
          setIsAddingToCart(true);
          const { selectedVariantId, selectedQuantity } = arcadeSelection.arcadeGameChoose;
          let didAddToCart = false;

          if (productRenderedLocation === "external") {
            const lines = {
              items: [
                {
                  merchandiseId: selectedVariantId,
                  quantity: selectedQuantity,
                },
              ],
            };

            const addToCart = await query(ADD_TO_CART_LINES, {
              cartId: headlessCartInfo.id,
              lines: lines.items,
            });

            const isAddedToCart = addToCart?.data?.cartLinesAdd;
            if (isAddedToCart) {
              didAddToCart = true;
              const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
              dispatch({
                type: "SET_CARTITEMS_HEADLESS",
                payload: {
                  headlessCart: [...cartItems] as ICartItemHeadless[],
                },
              });

              const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
              dispatch({
                type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                payload: {
                  headlessCartTotalAmount: cartTotalAmount,
                },
              });

              dispatch({
                type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
                payload: {
                  isHeadlessMiniCartIsShowing: true,
                },
              });
            }
          } else {
            let formData = {
              items: [
                {
                  id: getAdminIdFromStoreFrontId(selectedVariantId),
                  quantity: selectedQuantity,
                },
              ],
            };

            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(formData),
            });

            const cart = await response.json();
            if (cart) {
              didAddToCart = true;
              // Fetch updated cart data
              const cartResponse = await fetch("/cart.json");
              const cartData = await cartResponse.json();

              // Dispatch cart:updated event to notify all listeners
              const cartUpdatedEvent = new CustomEvent("cart:updated", {
                detail: { cart: cartData },
              });
              window.dispatchEvent(cartUpdatedEvent);

              miniCartCallback(true);
            }
          }
          setIsAddingToCart(false);
          if (didAddToCart) {
            dispatch({
              type: "SET_IS_SHOPIFY_CART_VARIANTS_LOADING",
              payload: { isShopifyCartVariantsLoaded: false },
            });
            // After adding arcade card, return to attraction selection page
            resetSelectedTrack();
            navigateWithParams(ROUTES.INDEX, undefined, undefined, { directionOverride: 1 });
          }
        } else if (isAllSessionsSelected) {
          // Add to cart first, then navigate to experience selection page
          setIsAddingToCart(true);
          dispatch({
            type: "SET_IS_PROCESSING_ADD_TO_CART",
            payload: { isProcessing: true, processingType: "adding" },
          });

          const result = await handleAddToCartClick(
            selectedVariants,
            currentQuantity,
            productRenderedLocation,
            headlessCart,
            headlessCartInfo,
            dispatch,
            false,
            visitorLocation,
            selectedTrack,
            {
              discountOffered: urlParams.discountOffered,
              discount20Applied: urlParams.discount20Applied,
              discount25Applied: urlParams.discount25Applied,
              qualifiedFor20: urlParams.qualifiedFor20,
              qualifiedFor25: urlParams.qualifiedFor25,
              qualifiedFor20OnThird: urlParams.qualifiedFor20OnThird,
              bkDiscountOffered: urlParams.bkDiscountOffered,
              bkDiscount20Applied: urlParams.bkDiscount20Applied,
              bkDiscount25Applied: urlParams.bkDiscount25Applied,
              bkQualifiedFor20: urlParams.bkQualifiedFor20,
              bkQualifiedFor25: urlParams.bkQualifiedFor25,
              bkQualifiedFor20OnThird: urlParams.bkQualifiedFor20OnThird,
            },
            editedQuantities, // Pass edited quantities for per-variant quantity overrides
          );

          if (result?.success) {
            // Clear edited quantities after successful add to cart
            clearEditedQuantities();
            // Clear selected sessions so a new set can be added
            clearSelectedVariantsForTrack();

            // Force shopifyCartVariants to refetch so newly-added items
            // include metafield + real quantityAvailable for dynamic pricing
            dispatch({
              type: "SET_IS_SHOPIFY_CART_VARIANTS_LOADING",
              payload: { isShopifyCartVariantsLoaded: false },
            });

            // Mark that items are available in cart
            dispatch({
              type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
              payload: { isAnyItemsAvailableInCart: true },
            });

            // Update cart total amount if needed
            if (productRenderedLocation !== "external") {
              const cartDataResponse = await fetch("/cart.json");
              const cartInfo = await cartDataResponse.json();

              if (cartInfo) {
                const { total_price, items_subtotal_price } = cartInfo;
                if (total_price || items_subtotal_price) {
                  dispatch({
                    type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                    payload: { headlessCartTotalAmount: total_price || items_subtotal_price },
                  });
                  dispatch({
                    type: "SET_IS_IN_CART_MODE",
                    payload: { isInCartMode: true },
                  });
                }
              }
            } else {
              dispatch({
                type: "SET_IS_IN_CART_MODE",
                payload: { isInCartMode: true },
              });
            }

            // Enable arcade banner after adding to cart
            dispatch({
              type: "SET_SHOW_ARCADE_BANNER",
              payload: {
                showArcadeBanner: true,
              },
            });

            clearSelectedVariantsForTrack();

            if (hasMoreTracksInQueue) {
              navigateToNextQueuedTrack();
            } else {
              if (multiTrackQueue.entries.length > 0) {
                dispatch({ type: "CLEAR_MULTI_TRACK_QUEUE" });
              }
              resetSelectedTrack();
              // Keep the processing/adding state on while the browser navigates so the
              // button doesn't flash back to its idle label mid-redirect.
              checkoutRedirect();
              return;
            }
          }

          dispatch({
            type: "SET_IS_PROCESSING_ADD_TO_CART",
            payload: { isProcessing: false, processingType: undefined },
          });
          setIsAddingToCart(false);
        } else if (nextInfo.pagePath) {
          if (bottomSummaryOnBeforeNavigateRef.current) {
            bottomSummaryOnBeforeNavigateRef.current();
          } else {
            navigateWithParams(nextInfo.pagePath);
          }
        }
      } catch (error) {
        console.error(error);
        dispatch({
          type: "SET_IS_PROCESSING_ADD_TO_CART",
          payload: { isProcessing: false, processingType: undefined },
        });
        setIsAddingToCart(false);
      }
    } else {
      if (bottomSummaryOnBeforeNavigateRef.current) {
        bottomSummaryOnBeforeNavigateRef.current();
      } else {
        navigateWithParams(nextInfo.pagePath);
      }
    }
  }, [
    urlParams.selectedScreen,
    selectedTrack,
    nextInfo,
    dispatch,
    arcadeSelection.arcadeGameChoose,
    productRenderedLocation,
    isMobile,
    headlessCartTotalAmount,
    headlessCartInfo,
    setIsTotalVisible,
    resetSelectedTrack,
    navigateWithParams,
    selectedVariants,
    currentQuantity,
    headlessCart,
    visitorLocation,
    urlParams.discountOffered,
    urlParams.discount20Applied,
    urlParams.discount25Applied,
    urlParams.qualifiedFor20,
    urlParams.qualifiedFor25,
    urlParams.qualifiedFor20OnThird,
    urlParams.bkDiscountOffered,
    urlParams.bkDiscount20Applied,
    urlParams.bkDiscount25Applied,
    urlParams.bkQualifiedFor20,
    urlParams.bkQualifiedFor25,
    urlParams.bkQualifiedFor20OnThird,
    editedQuantities,
    clearEditedQuantities,
    isAllSessionsSelected,
    clearSelectedVariantsForTrack,
    hasMoreTracksInQueue,
    navigateToNextQueuedTrack,
    multiTrackQueue.entries.length,
    checkoutRedirect,
  ]);

  const handleCheckout = useCallback(async () => {
    if (!hasPendingSelections) {
      checkoutRedirect();
      return;
    }

    try {
      setIsAddingToCart(true);
      dispatch({
        type: "SET_IS_PROCESSING_ADD_TO_CART",
        payload: { isProcessing: true, processingType: "adding" },
      });

      const result = await handleAddToCartClick(
        selectedVariants,
        currentQuantity,
        productRenderedLocation,
        headlessCart,
        headlessCartInfo,
        dispatch,
        false,
        visitorLocation,
        selectedTrack,
        {
          discountOffered: urlParams.discountOffered,
          discount20Applied: urlParams.discount20Applied,
          discount25Applied: urlParams.discount25Applied,
          qualifiedFor20: urlParams.qualifiedFor20,
          qualifiedFor25: urlParams.qualifiedFor25,
          qualifiedFor20OnThird: urlParams.qualifiedFor20OnThird,
          bkDiscountOffered: urlParams.bkDiscountOffered,
          bkDiscount20Applied: urlParams.bkDiscount20Applied,
          bkDiscount25Applied: urlParams.bkDiscount25Applied,
          bkQualifiedFor20: urlParams.bkQualifiedFor20,
          bkQualifiedFor25: urlParams.bkQualifiedFor25,
          bkQualifiedFor20OnThird: urlParams.bkQualifiedFor20OnThird,
        },
        editedQuantities,
      );

      if (result?.success) {
        clearEditedQuantities();
        clearSelectedVariantsForTrack();

        dispatch({
          type: "SET_IS_SHOPIFY_CART_VARIANTS_LOADING",
          payload: { isShopifyCartVariantsLoaded: false },
        });
        dispatch({
          type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
          payload: { isAnyItemsAvailableInCart: true },
        });
      }

      dispatch({
        type: "SET_IS_PROCESSING_ADD_TO_CART",
        payload: { isProcessing: false, processingType: undefined },
      });
      setIsAddingToCart(false);

      checkoutRedirect();
    } catch (error) {
      console.error(error);
      dispatch({
        type: "SET_IS_PROCESSING_ADD_TO_CART",
        payload: { isProcessing: false, processingType: undefined },
      });
      setIsAddingToCart(false);
    }
  }, [
    hasPendingSelections,
    productRenderedLocation,
    headlessCartInfo,
    selectedVariants,
    currentQuantity,
    headlessCart,
    dispatch,
    visitorLocation,
    selectedTrack,
    urlParams.discountOffered,
    urlParams.discount20Applied,
    urlParams.discount25Applied,
    urlParams.qualifiedFor20,
    urlParams.qualifiedFor25,
    urlParams.qualifiedFor20OnThird,
    urlParams.bkDiscountOffered,
    urlParams.bkDiscount20Applied,
    urlParams.bkDiscount25Applied,
    urlParams.bkQualifiedFor20,
    urlParams.bkQualifiedFor25,
    urlParams.bkQualifiedFor20OnThird,
    editedQuantities,
    clearEditedQuantities,
    clearSelectedVariantsForTrack,
    checkoutRedirect,
  ]);

  const handleSessionChange = useCallback(
    (sessionId: string) => {
      setEditSessionTime(sessionId);
      // Use getSessionQuantity to get the correct quantity (from cart, editedQuantities, or default)
      const actualPlayerCount = getSessionQuantity(sessionId, currentQuantity);
      setEditPlayerCount(actualPlayerCount);
    },
    [getSessionQuantity, currentQuantity, setEditSessionTime, setEditPlayerCount],
  );

  const isBattleKartTrack = selectedTrack === "BATTLE_KART_TRACK" || isPathwayBattleKart;

  const stickyBg = pathwayStyle.stickySummary?.background ?? "#393B41";
  const stickyDesktopAccent =
    pathwayStyle.stickySummary?.desktopAccent ?? (isBattleKartTrack ? "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)" : "#9d62fe");
  const stickyRadius = pathwayStyle.stickySummary?.borderRadius ?? "15px";

  const generatedBgColor = useMemo(() => {
    // Base + desktop accent resolved in App.css from the vars in stickyBarStyle.
    return "booking-sticky-bar";
  }, []);

  const secondaryBgColor = generatedBgColor;

  const mobileCollapsedBarBg = useMemo(() => {
    if (effectiveMobileNavVariant === "experience") {
      return "booking-sticky-bar booking-sticky-bar--transparent";
    }
    return secondaryBgColor;
  }, [effectiveMobileNavVariant, secondaryBgColor]);

  const stickyBarClass = useMemo(
    () => (isTotalVisible ? "booking-sticky-bar booking-sticky-bar--expanded" : mobileCollapsedBarBg),
    [isTotalVisible, mobileCollapsedBarBg],
  );

  // Vars only — .booking-sticky-bar resolves them so its desktop-accent media query can
  // still win. An inline `background` here would outrank the breakpoint rule entirely.
  const stickyBarStyle = useMemo(
    () =>
      ({
        ["--booking-sticky-bg" as string]: stickyBg,
        ["--booking-sticky-radius" as string]: stickyRadius,
        ["--booking-sticky-desktop-accent" as string]: stickyDesktopAccent,
      }) as React.CSSProperties,
    [stickyBg, stickyDesktopAccent, stickyRadius],
  );

  // Theme CTA background (CSS var also set by usePathwayStyling). Class kept empty so
  // NavigationButtons can rely on --booking-cta-bg via the booking-cta-pill class.
  const EXPERIENCE_PILL_GRADIENT_BG = "booking-cta-pill";

  const generateNextButtonBgColor = useMemo(() => {
    return "booking-cta-pill";
  }, []);

  // Sticky summary click handlers - track the interaction, then delegate to the underlying action.
  const handlePreviousClick = useCallback(() => {
    trackStickySummary({ action: "back", label: previousInfo.pageName });
    navigateWithParams(previousInfo.pagePath);
  }, [trackStickySummary, previousInfo.pageName, previousInfo.pagePath, navigateWithParams]);

  const handleStickyNextClick = useCallback(() => {
    if (!nextInfo) return;
    trackStickySummary({ action: "next", label: nextInfo.pageName });
    return handleNextButtonClick();
  }, [trackStickySummary, nextInfo, handleNextButtonClick]);

  const handleStickyCheckout = useCallback(() => {
    trackStickySummary({ action: "checkout" });
    return handleCheckout();
  }, [trackStickySummary, handleCheckout]);

  const handleTrackTabSelect = useCallback(
    (index: number) => {
      trackStickySummary({ action: "track_tab_select", summaryTrack: trackEntries[index]?.[0] ?? "unknown" });
      setCurrentPage(index);
    },
    [trackStickySummary, trackEntries, setCurrentPage],
  );

  const handleStickyRemoveClick = useCallback(
    (...args: Parameters<typeof handleRemoveSessionClick>) => {
      const [, , , trackKey] = args;
      trackStickySummary({ action: "remove_session_click", summaryTrack: trackKey });
      handleRemoveSessionClick(...args);
    },
    [trackStickySummary, handleRemoveSessionClick],
  );

  const handleStickyConfirmRemove = useCallback(() => {
    if (sessionToRemove) trackStickySummary({ action: "remove_session_confirm", summaryTrack: sessionToRemove.trackKey });
    return handleConfirmRemove();
  }, [trackStickySummary, sessionToRemove, handleConfirmRemove]);

  const handleStickyCancelRemove = useCallback(() => {
    if (sessionToRemove) trackStickySummary({ action: "remove_session_cancel", summaryTrack: sessionToRemove.trackKey });
    handleCancelRemove();
  }, [trackStickySummary, sessionToRemove, handleCancelRemove]);

  // Handle session click - navigate to track selection page and switch to that track in summary
  const handleSessionClickWithNavigation = useCallback(
    (sessionId: string, trackKey: string) => {
      trackStickySummary({ action: "session_click", summaryTrack: trackKey });
      setSelectedSessionId(sessionId);

      // Find the index of the clicked track in trackEntries and switch to it
      const trackIndex = trackEntries.findIndex(([key]) => key === trackKey);
      if (trackIndex !== -1 && trackIndex !== currentPage) {
        setCurrentPage(trackIndex);
      }

      // Find the session to extract its date
      let selectedDateParam: string | undefined;
      const trackData = trackEntries.find(([key]) => key === trackKey);
      if (trackData) {
        const session = trackData[1].sessions.find((s: any) => s.variantsSelectedObject.id === sessionId);
        if (session) {
          // Extract date from product title (format: "Tuesday, 21st January 2025 | 10:00 AM")
          const productTitle = session.variantsSelectedObject?.product?.title || session.variantsSelectedObject?.title || "";
          const sessionDate = parseDateFromTitle(productTitle);
          if (sessionDate && sessionDate.isValid) {
            // Convert to yyLLdd format (e.g., "260123" for January 23, 2026)
            selectedDateParam = sessionDate.toFormat("yyLLdd");
          }
        }
      }

      // Navigate back to the selection page for this track with the session's date
      navigateToTrackSelection(trackKey, selectedDateParam);
    },
    [trackStickySummary, setSelectedSessionId, navigateToTrackSelection, trackEntries, currentPage, setCurrentPage],
  );

  const portalTarget = usePortalTarget("bottom-summary-portal");

  // Sticky summary motion — decelerate on open, ease-in on close (PM feedback).
  const stickySummaryOpenMotion = "tw-duration-[450ms] tw-ease-[cubic-bezier(0.16,1,0.3,1)]";
  const stickySummaryCloseMotion = "tw-duration-[520ms] tw-ease-[cubic-bezier(0.4,0,0.9,1)]";
  const stickySummaryMotion = isTotalVisible ? stickySummaryOpenMotion : stickySummaryCloseMotion;

  const stickySummaryPanelClass = `tw-absolute tw-right-0 tw-bottom-full tw-w-full desktop:tw-w-[550px] tw-overflow-hidden tw-rounded-tl-[15px] tw-rounded-tr-[15px] booking-sticky-surface tw-will-change-[opacity,transform] tw-transition-[opacity,transform] ${stickySummaryMotion} ${
    isTotalVisible
      ? "tw-pointer-events-auto tw-translate-y-0 tw-opacity-100"
      : "tw-pointer-events-none tw-translate-y-2 tw-opacity-0"
  }`;

  const stickySummaryHeightClass = `tw-grid tw-transition-[grid-template-rows] tw-will-change-[grid-template-rows] ${stickySummaryMotion} ${
    isTotalVisible ? "tw-grid-rows-[1fr]" : "tw-grid-rows-[0fr]"
  }`;

  // On the date-time step, cap the expanded cart so session tiles keep usable space.
  const mobileExpandedPanelMaxHeightClass =
    isMobile && isDateTimeStep ? "tw-max-h-[min(42vh,280px)]" : "tw-max-h-[calc(100vh-96px)] desktop:tw-max-h-[calc(100vh-60px)]";

  const content = (
    <div
      ref={stickyRootRef}
      className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[100]"
      style={{ position: "fixed", bottom: 0, left: 0, right: 0 }}
    >
      <div className="tw-flex tw-flex-col desktop:tw-flex-row tw-h-full tw-items-center">
        {/* Desktop Navigation - hidden on mobile/tablet (including iPad Pro) */}
        {showDesktopNavBar && (
          <div
            className={`tw-hidden desktop:tw-flex tw-flex-1 ${
              isOnExperienceSelectionPage ? "tw-bg-transparent tw-px-4 tw-pb-[20px]" : "booking-sticky-surface md:tw-p-4 md:tw-h-[86px]"
            } tw-items-center tw-justify-center tw-order-2 desktop:tw-order-1 tw-w-full`}
            ref={summaryRef}
          >
            <div className={`tw-flex tw-justify-center ${shouldShowSummaryBox && !isOnExperienceSelectionPage ? "xl:tw-ml-[350px]" : ""}`}>
              <NavigationButtons
                previousInfo={previousInfo}
                nextInfo={nextInfo}
                isMobile={isMobile}
                isAddingToCart={isAddingToCart}
                generateNextButtonBgColor={generateNextButtonBgColor}
                onPreviousClick={handlePreviousClick}
                onNextClick={handleStickyNextClick}
                showBackButton={showDesktopBackButton}
                showCheckoutButton={showDesktopCheckoutButton}
                onCheckoutClick={handleStickyCheckout}
                showCheckoutChevron={!isOnExperienceSelectionPage}
              />
            </div>
          </div>
        )}

        {/* Mobile/Tablet Navigation - shown when no summary box (no items in cart) */}
        {!shouldShowSummaryBox && (
          <div
            className={`desktop:tw-hidden tw-w-full tw-order-1 ${
              effectiveMobileNavVariant === "experience" ? "tw-bg-transparent tw-pb-[10px]" : generatedBgColor
            }`}
            style={effectiveMobileNavVariant === "experience" ? undefined : stickyBarStyle}
          >
            <SummaryHeader
              isTotalVisible={false}
              totalAmount={totalAmount}
              totalAmountDisplay={totalAmountDisplay}
              generatedBgColor={generatedBgColor}
              onToggle={toggleTotal}
              previousInfo={previousInfo}
              nextInfo={nextInfo}
              onPreviousClick={handlePreviousClick}
              onNextClick={handleStickyNextClick}
              isAddingToCart={isAddingToCart}
              productRenderedLocation={productRenderedLocation}
              checkoutUrl={headlessCartInfo.checkoutUrl}
              selectedTrack={selectedTrack}
              showChevron={false}
              isAnyItemsInCart={isAnyItemsAvailableInCart}
              showMobileCheckout={showMobileCheckoutOnFooter}
              hasPendingSelections={hasPendingSelections}
              onCheckout={handleStickyCheckout}
              mobileNavVariant={effectiveMobileNavVariant}
              showMobileNextButton={showNextButton}
              showMobileCartToggle={!isOnExperienceSelectionPage}
              showCartToggleInStandalone={isRacingGamesTrackAgeStep}
              showCheckoutChevron={!isOnExperienceSelectionPage}
              actionPillBgOverride={isOnExperienceSelectionPage ? EXPERIENCE_PILL_GRADIENT_BG : undefined}
              cartItemCount={cartItemCount}
              isDateTimeStep={isDateTimeStep}
              actionAlignRef={nav.actionAlignRef}
              viewSummaryLabel={bottomSummaryConfig.viewLabel}
              hideSummaryLabel={bottomSummaryConfig.hideLabel}
            />
          </div>
        )}

        {shouldShowSummaryBox && (
          <div
            className={`tw-flex tw-items-center tw-justify-start tw-relative tw-order-1 desktop:tw-order-2 tw-w-full desktop:tw-w-auto desktop:tw-ml-auto tw-h-full ${
              isOnExperienceSelectionPage ? "desktop:tw-hidden" : ""
            }`}
          >
            <div className={stickySummaryPanelClass} data-sticky-summary-panel>
              <div className={stickySummaryHeightClass}>
                <div className="tw-min-h-0 tw-overflow-hidden">
                  <div className="tw-flex tw-flex-col tw-gap-1">
                <div className="tw-hidden desktop:tw-block">
                  <SummaryHeader
                    headerContext="popup"
                    isTotalVisible={isTotalVisible}
                    totalAmount={totalAmount}
                    totalAmountDisplay={totalAmountDisplay}
                    generatedBgColor={generatedBgColor}
                    onToggle={toggleTotal}
                    previousInfo={previousInfo}
                    nextInfo={nextInfo}
                    onPreviousClick={handlePreviousClick}
                    onNextClick={handleStickyNextClick}
                    isAddingToCart={isAddingToCart}
                    productRenderedLocation={productRenderedLocation}
                    checkoutUrl={headlessCartInfo.checkoutUrl}
                    selectedTrack={selectedTrack}
                    isAnyItemsInCart={isAnyItemsAvailableInCart}
                    showMobileCheckout={showMobileCheckoutOnFooter}
                    hasPendingSelections={hasPendingSelections}
                    onCheckout={handleStickyCheckout}
                    mobileNavVariant={effectiveMobileNavVariant}
                    showMobileNextButton={showNextButton}
                    showMobileCartToggle={!isOnExperienceSelectionPage}
                    showCheckoutChevron={!isOnExperienceSelectionPage}
                    actionPillBgOverride={isOnExperienceSelectionPage ? EXPERIENCE_PILL_GRADIENT_BG : undefined}
                    cartItemCount={cartItemCount}
                    isDateTimeStep={isDateTimeStep}
                    actionAlignRef={nav.actionAlignRef}
                    viewSummaryLabel={bottomSummaryConfig.viewLabel}
                    hideSummaryLabel={bottomSummaryConfig.hideLabel}
                  />
                </div>

                <div
                  className={`tw-flex tw-flex-col tw-h-auto ${mobileExpandedPanelMaxHeightClass} tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-gray-500 tw-scrollbar-track-transparent ${
                    isEditMode ? "tw-pb-12" : "tw-pb-0"
                  }`}
                >
                  {!isSummaryEmpty ? (
                    <div className="tw-flex tw-flex-col">
                      {trackEntries.length > 0 && (
                        <>
                          {/* Track Selector Dropdown - above the boxes */}
                          {!isEditMode && (
                            <TrackSelector
                              trackEntries={trackEntries}
                              currentTrackIndex={currentPage}
                              onTrackChange={handleTrackTabSelect}
                              // Multi-track: minimise sits on the track pills row.
                              trailingContent={trackEntries.length > 1 && isTotalVisible ? mobileExpandedMinimiseButton : undefined}
                            />
                          )}

                          {/* Edit mode hides SessionPills date headers — keep minimise top-right. */}
                          {isEditMode && isTotalVisible && (
                            <div className="desktop:tw-hidden tw-flex tw-justify-end tw-px-[8px] tw-pt-[6px]">{mobileExpandedMinimiseButton}</div>
                          )}

                          <div className="tw-px-[8px] tw-pt-1 tw-pb-1">
                            {(() => {
                              const [trackKey, trackData] = trackEntries[currentPage] || trackEntries[0];

                              return (
                                <div className="tw-w-full">
                                  <TrackCard
                                    trackKey={trackKey}
                                    trackType={trackData.trackType}
                                    sessions={trackData.sessions}
                                    isEditMode={isEditMode}
                                    editingTrackKey={editingTrackKey}
                                    editSessionTime={editSessionTime}
                                    editPlayerCount={editPlayerCount}
                                    isSaving={isSaving}
                                    selectedSessionId={selectedSessionId}
                                    cartData={cartData}
                                    productRenderedLocation={productRenderedLocation}
                                    defaultQuantity={currentQuantity}
                                    getSessionQuantity={getSessionQuantity}
                                    seatsTitle={seatsTitleByTrack[trackKey]}
                                    onSessionChange={handleSessionChange}
                                    onIncrement={incrementPlayers}
                                    onDecrement={decrementPlayers}
                                    onSaveChanges={handleSaveChanges}
                                    onCancelEdit={handleCancelEdit}
                                    onSessionClick={handleSessionClickWithNavigation}
                                    onRemoveClick={handleStickyRemoveClick}
                                    onEditDate={(date) => navigateToTrackSelection(trackKey)}
                                    // Single-track: minimise shares the first date / section header row.
                                    headerTrailingContent={
                                      !isEditMode && trackEntries.length <= 1 && isTotalVisible
                                        ? mobileExpandedMinimiseButton
                                        : undefined
                                    }
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      )}

                    </div>
                  ) : null}
                </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`tw-flex tw-items-center tw-justify-between tw-w-full desktop:tw-w-[550px] tw-overflow-visible tw-transition-colors ${stickySummaryMotion} ${stickyBarClass} ${
                effectiveMobileNavVariant === "experience"
                  ? // Expanded: modest gap under the cart pills. Collapsed: keep breathing room above Next.
                    isTotalVisible
                      ? "tw-pt-[6px] tw-pb-[8px] desktop:tw-pt-0 desktop:tw-pb-0"
                      : "tw-pt-[8px] tw-pb-[10px] desktop:tw-pt-0 desktop:tw-pb-0"
                  : ""
              } desktop:tw-px-[8px]`}
              style={{
                // The experience footer is a floating transparent bar that relies on its own
                // pt/pb spacing — don't pin it to the fixed cart-bar height (which would swallow
                // the bottom padding on iPad landscape, where !isMobile is true).
                height: !isMobile && summaryHeight > 0 && effectiveMobileNavVariant !== "experience" ? `${summaryHeight}px` : "auto",
                ...stickyBarStyle,
              }}
              onClick={!isTotalVisible && !isBelowDesktop ? () => toggleTotal("bar") : undefined}
            >
              {/* Mobile/tablet (below the desktop breakpoint) keeps the header in the footer so
                  Next/Checkout + cart toggle stay visible while the panel expands above. Only the
                  true desktop (≥1380px) hands off to the popup header + desktop nav. */}
              {(!isTotalVisible || isBelowDesktop) && (
                <SummaryHeader
                  isTotalVisible={isBelowDesktop ? isTotalVisible : false}
                  totalAmount={totalAmount}
                  totalAmountDisplay={totalAmountDisplay}
                  generatedBgColor={generatedBgColor}
                  onToggle={toggleTotal}
                  previousInfo={previousInfo}
                  nextInfo={nextInfo}
                  onPreviousClick={handlePreviousClick}
                  onNextClick={handleStickyNextClick}
                  isAddingToCart={isAddingToCart}
                  productRenderedLocation={productRenderedLocation}
                  checkoutUrl={headlessCartInfo.checkoutUrl}
                  selectedTrack={selectedTrack}
                  isAnyItemsInCart={isAnyItemsAvailableInCart}
                  showMobileCheckout={showMobileCheckoutOnFooter}
                  hasPendingSelections={hasPendingSelections}
                  onCheckout={handleStickyCheckout}
                  mobileNavVariant={effectiveMobileNavVariant}
                  showMobileNextButton={showNextButton}
                  showMobileCartToggle={!isOnExperienceSelectionPage}
                  showCheckoutChevron={!isOnExperienceSelectionPage}
                  actionPillBgOverride={isOnExperienceSelectionPage ? EXPERIENCE_PILL_GRADIENT_BG : undefined}
                  cartItemCount={cartItemCount}
                  isDateTimeStep={isDateTimeStep}
                  actionAlignRef={nav.actionAlignRef}
                  viewSummaryLabel={bottomSummaryConfig.viewLabel}
                  hideSummaryLabel={bottomSummaryConfig.hideLabel}
                />
              )}
            </div>
          </div>
        )}

        {showRemoveConfirmation && sessionToRemove && (
          <RemoveConfirmationModal sessionToRemove={sessionToRemove} isRemoving={isRemoving} onConfirm={handleStickyConfirmRemove} onCancel={handleStickyCancelRemove} />
        )}
      </div>
    </div>
  );

  return portalTarget ? createPortal(content, portalTarget) : content;
};

/**
 * Single persistent renderer for the bottom summary / nav footer.
 *
 * Mounted ONCE at the top level (see NewUI in Router.tsx), outside the animated
 * <Routes> tree — exactly like ProgressBar. Because it never unmounts on route
 * change, the Next button no longer flickers between page transitions.
 *
 * It is driven entirely by `bottomSummaryNav` in global state, which each screen
 * registers via the `BottomSummary` registrar below. When no footer is registered
 * (home, arcade experience/cart) the renderer returns null.
 */
export const GlobalBottomSummary = () => {
  const { bottomSummaryNav } = useTrackContext();
  if (!bottomSummaryNav) return null;
  return <BottomSummaryView nav={bottomSummaryNav} />;
};

/**
 * Per-screen registrar. Screens keep rendering `<BottomSummary previousInfo nextInfo .../>`
 * exactly as before, but instead of mounting the heavy UI itself it just registers
 * its nav config into global state (synchronously, before paint) for the single
 * persistent GlobalBottomSummary to render. Renders nothing.
 */
const BottomSummary = ({ previousInfo, nextInfo, mobileNavVariant, actionAlignRef }: IBottomSummaryProps) => {
  useRegisterBottomSummary({
    previousInfo,
    nextInfo,
    mobileNavVariant,
    actionAlignRef,
  });
  return null;
};

export default BottomSummary;
